import { useState, useEffect } from "react";
import axios from "axios";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Clock,
  CalendarDays,
  MapPin,
  Briefcase,
  User,
  Phone,
  Mail,
  FileText,
  Send,
  CheckCircle2,
  Banknote,
  Users,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ShiftsPage() {
  const [shifts, setShifts] = useState([]);
  const [selectedShift, setSelectedShift] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [cvFile, setCvFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    applicant_name: "",
    email: "",
    phone: "",
    experience: "",
    registration: "",
    message: "",
  });

  useEffect(() => {
    axios.get(`${API}/shifts?status=open`).then((r) => setShifts(r.data));
  }, []);

  const handleApply = (shift) => {
    setSelectedShift(shift);
    setSubmitted(false);
    setForm({ applicant_name: "", email: "", phone: "", experience: "", registration: "", message: "" });
    setTimeout(() => {
      document.getElementById("shift-apply-form")?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.applicant_name || !form.email || !form.phone || !form.experience) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      let cv_url = "";
      if (cvFile) {
        setUploading(true);
        const formData = new FormData();
        formData.append("file", cvFile);
        const uploadRes = await axios.post(`${API}/upload-cv`, formData, { headers: { "Content-Type": "multipart/form-data" } });
        cv_url = uploadRes.data.url;
        setUploading(false);
      }
      await axios.post(`${API}/shift-applications`, {
        ...form,
        shift_id: selectedShift.id,
        shift_title: `${selectedShift.role} - ${selectedShift.title}`,
        shift_date: selectedShift.date,
        cv_url,
      });
      setSubmitted(true);
      toast.success("Application submitted successfully!");
      // Refresh shifts
      const res = await axios.get(`${API}/shifts?status=open`);
      setShifts(res.data);
    } catch {
      toast.error("Failed to submit application. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    } catch {
      return dateStr;
    }
  };

  const openShifts = shifts.filter((s) => s.slots_filled < s.slots_available);

  return (
    <div className="min-h-screen bg-stone-50 pt-24 pb-16 px-4" data-testid="shifts-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="bg-orange-100 text-orange-800 border-0 mb-4 px-4 py-1.5 rounded-full text-sm font-medium">
            Available Shifts
          </Badge>
          <h1 className="font-[Outfit] text-3xl sm:text-4xl lg:text-5xl font-bold text-stone-900 tracking-tight mb-4">
            Agency Staff Shifts
          </h1>
          <p className="text-base md:text-lg text-stone-500 max-w-2xl mx-auto">
            Browse available shifts at IV Medics and Diagnostics Clinic. Apply online and our team will get back to you.
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-6">
            <div className="flex items-center gap-2 bg-white border border-stone-100 rounded-full px-4 py-2 text-sm text-stone-600 shadow-sm">
              <MapPin className="w-4 h-4 text-green-700" />
              Bodija, Ibadan
            </div>
            <div className="flex items-center gap-2 bg-white border border-stone-100 rounded-full px-4 py-2 text-sm text-stone-600 shadow-sm">
              <Users className="w-4 h-4 text-green-700" />
              {openShifts.length} Shift{openShifts.length !== 1 ? "s" : ""} Available
            </div>
          </div>
        </div>

        {/* Shifts List */}
        {openShifts.length === 0 && (
          <div className="bg-white rounded-2xl border border-stone-100 p-12 text-center mb-12">
            <CalendarDays className="w-14 h-14 text-stone-300 mx-auto mb-4" />
            <h3 className="font-[Outfit] text-xl font-semibold text-stone-900 mb-2">No Shifts Available</h3>
            <p className="text-stone-500 text-sm">Check back soon for new shift opportunities, or visit our <a href="/careers" className="text-green-700 underline">Careers page</a> for permanent roles.</p>
          </div>
        )}

        <div className="space-y-4 mb-12">
          {openShifts.map((shift) => {
            const spotsLeft = shift.slots_available - shift.slots_filled;
            return (
              <div
                key={shift.id}
                data-testid={`shift-${shift.id}`}
                className={`bg-white rounded-2xl border p-6 sm:p-8 transition-all hover:shadow-md ${
                  selectedShift?.id === shift.id ? "border-green-600 shadow-md" : "border-stone-100 hover:border-green-200"
                }`}
              >
                <div className="flex flex-col sm:flex-row sm:items-start gap-5">
                  <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Briefcase className="w-6 h-6 text-green-700" />
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                      <h3 className="font-[Outfit] text-lg font-bold text-stone-900">{shift.role}</h3>
                      <Badge className="bg-green-100 text-green-800 border-0 rounded-full text-xs px-3">
                        {spotsLeft} spot{spotsLeft !== 1 ? "s" : ""} left
                      </Badge>
                    </div>
                    <p className="text-sm text-stone-900 font-medium mb-2">{shift.title}</p>
                    {shift.description && <p className="text-sm text-stone-500 mb-4">{shift.description}</p>}
                    <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-stone-600 mb-4">
                      <span className="flex items-center gap-1.5">
                        <CalendarDays className="w-4 h-4 text-green-700" />
                        {formatDate(shift.date)}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Clock className="w-4 h-4 text-green-700" />
                        {shift.start_time} - {shift.end_time}
                      </span>
                      {shift.rate && (
                        <span className="flex items-center gap-1.5">
                          <Banknote className="w-4 h-4 text-green-700" />
                          {shift.rate}
                        </span>
                      )}
                    </div>
                    <Button
                      data-testid={`apply-shift-${shift.id}`}
                      onClick={() => handleApply(shift)}
                      className="bg-green-700 hover:bg-green-800 text-white rounded-full px-6 gap-2"
                    >
                      Apply for This Shift <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Application Form */}
        {selectedShift && !submitted && (
          <div id="shift-apply-form" className="bg-white rounded-3xl border border-stone-100 p-6 sm:p-10 shadow-sm mb-12" data-testid="shift-application-form">
            <h2 className="font-[Outfit] text-2xl font-bold text-stone-900 mb-1">
              Apply for Shift
            </h2>
            <div className="flex flex-wrap gap-3 mb-6 text-sm text-stone-600">
              <span className="flex items-center gap-1.5"><Briefcase className="w-4 h-4 text-green-700" />{selectedShift.role}</span>
              <span className="flex items-center gap-1.5"><CalendarDays className="w-4 h-4 text-green-700" />{formatDate(selectedShift.date)}</span>
              <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-green-700" />{selectedShift.start_time} - {selectedShift.end_time}</span>
            </div>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <Label className="text-stone-700 text-sm font-medium mb-2 flex items-center gap-2">
                    <User className="w-4 h-4" /> Full Name *
                  </Label>
                  <Input data-testid="shift-name-input" placeholder="Your full name" value={form.applicant_name} onChange={(e) => setForm({ ...form, applicant_name: e.target.value })} className="bg-stone-50 rounded-xl border-stone-200 h-12" />
                </div>
                <div>
                  <Label className="text-stone-700 text-sm font-medium mb-2 flex items-center gap-2">
                    <Mail className="w-4 h-4" /> Email Address *
                  </Label>
                  <Input data-testid="shift-email-input" type="email" placeholder="your@email.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-stone-50 rounded-xl border-stone-200 h-12" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <Label className="text-stone-700 text-sm font-medium mb-2 flex items-center gap-2">
                    <Phone className="w-4 h-4" /> Phone Number *
                  </Label>
                  <Input data-testid="shift-phone-input" placeholder="08012345678" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-stone-50 rounded-xl border-stone-200 h-12" />
                </div>
                <div>
                  <Label className="text-stone-700 text-sm font-medium mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4" /> Professional Registration
                  </Label>
                  <Input data-testid="shift-registration-input" placeholder="e.g. MDCN Reg No., NMCN, MLSCN..." value={form.registration} onChange={(e) => setForm({ ...form, registration: e.target.value })} className="bg-stone-50 rounded-xl border-stone-200 h-12" />
                </div>
              </div>
              <div>
                <Label className="text-stone-700 text-sm font-medium mb-2 flex items-center gap-2">
                  <Briefcase className="w-4 h-4" /> Relevant Experience *
                </Label>
                <Textarea data-testid="shift-experience-input" placeholder="Describe your experience relevant to this shift..." value={form.experience} onChange={(e) => setForm({ ...form, experience: e.target.value })} className="bg-stone-50 rounded-xl border-stone-200 min-h-[100px]" />
              </div>
              <div>
                <Label className="text-stone-700 text-sm font-medium mb-2 flex items-center gap-2">
                  <FileText className="w-4 h-4" /> Additional Message (Optional)
                </Label>
                <Textarea data-testid="shift-message-input" placeholder="Anything else you'd like us to know..." value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="bg-stone-50 rounded-xl border-stone-200 min-h-[80px]" />
              </div>
              <div>
                <Label className="text-stone-700 text-sm font-medium mb-2 flex items-center gap-2">
                  <FileText className="w-4 h-4" /> Upload CV / Resume
                </Label>
                <div className="flex items-center gap-3">
                  <label className="flex-1 cursor-pointer">
                    <div className={`bg-stone-50 border-2 border-dashed rounded-xl p-4 text-center transition-all hover:border-green-400 ${cvFile ? "border-green-500 bg-green-50" : "border-stone-200"}`}>
                      <p className="text-sm text-stone-600">{cvFile ? cvFile.name : "Click to select PDF, DOC, or DOCX file"}</p>
                      {cvFile && <p className="text-xs text-green-600 mt-1">File selected</p>}
                    </div>
                    <input data-testid="shift-cv-input" type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" className="hidden" onChange={(e) => setCvFile(e.target.files[0] || null)} />
                  </label>
                  {cvFile && (
                    <Button type="button" variant="outline" size="sm" onClick={() => setCvFile(null)} className="rounded-xl border-stone-200 text-xs">Remove</Button>
                  )}
                </div>
              </div>
              <Button data-testid="shift-submit-btn" type="submit" disabled={submitting} className="bg-orange-600 hover:bg-orange-700 text-white rounded-full px-8 py-6 gap-2 text-base shadow-lg">
                {submitting ? "Submitting..." : "Submit Application"} <CheckCircle2 className="w-4 h-4" />
              </Button>
            </form>
          </div>
        )}

        {/* Success Message */}
        {submitted && selectedShift && (
          <div className="bg-white rounded-3xl border border-green-100 p-8 text-center mb-12" data-testid="shift-application-success">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-700" />
            </div>
            <h3 className="font-[Outfit] text-xl font-bold text-stone-900 mb-2">Application Submitted!</h3>
            <p className="text-stone-600 text-sm mb-1">Your application for <strong>{selectedShift.role}</strong> on <strong>{formatDate(selectedShift.date)}</strong> has been received.</p>
            <p className="text-stone-500 text-sm">Our team will review and contact you via email or phone. For enquiries, reach us at <strong>info@ivmedicsclinic.com</strong></p>
          </div>
        )}
      </div>
    </div>
  );
}
